import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { numericFormatter } from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import { useFindAllCompaniesContactsQuery, useFindAllPeopleContactsQuery, useSoftBulkContactsMutation } from "../../../features/contactsApi";
import { useFindAllCustomMailsQuery, useFindEmailsHistoryQuery } from "../../../features/mailsApi";
import { useFindOneTargetListQuery, useUpdateTargetListMutation } from "../../../features/targetlistApi";
import { addBannerWithTimeout } from "../../lib/centralizedBanner/slice";
import { ContactDTO, ContactType, FindAllContactsFilters, FindContactsTargetResponse } from "../../lib/filters/dto";
import { initialStateContactsFilters } from "../../lib/filters/filtersSlice";
import { keycloak } from "../../lib/keycloak";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import { TrashIcon } from "../../ui/icons/trash";
import { Layout } from "../../ui/layout";
import Button from "../../ui/molecules/button";
import { Spinner } from "../../ui/molecules/spinner";
import { ConfirmModal } from "../../ui/organisms/confirmModal.tsx";
import { MenuItems } from "../../ui/organisms/navbar/dto";
import { TabMenu } from "../../ui/organisms/tabs/tabMenu";
import { DeleteBulkContacts } from "../database/commons/deleteBulk";
import { TargetListPageEnum } from "./dto";
import { ActionsCompaniesTargetList } from "./editTargetListDetails/companies/list/actions";
import { EditTargetList } from "./editTargetListDetails/index";
import { ActionsPeopleTargetList } from "./editTargetListDetails/people/list/actions";
import { UpdateTargetListFormValues, updateTargetListSchema } from "./editTargetListDetails/validation";
import { setTargetListPage } from "./slice";
import { PersonDTO } from "../database/people/dto";

export function EditTargetListPage() {
  const targetId = useParams().targetId!;
  const tab = useParams().tab!;
  const projectId = useParams().projectId!;
  const navigate = useNavigate();

  const { data: targetListData, isLoading: isLoadingTargetList } = useFindOneTargetListQuery(targetId, {
    skip: !targetId,
  });
  const project = targetListData?.project;
  const targetList = targetListData?.targetList;
  const counters = targetListData?.counters;
  const contactsIds = targetListData?.contactsIds;

  const [peopleFilters, setPeopleFilters] = useState<FindAllContactsFilters>(initialStateContactsFilters['target'][ContactType.PERSON]);
  const { data: contactPeopleData, isLoading: isLoadingContactPeople } = useFindAllPeopleContactsQuery({ filters: peopleFilters, targetList: targetId });

  const [companiesFilters, setCompaniesFilters] = useState<FindAllContactsFilters>(initialStateContactsFilters['target'][ContactType.COMPANY]);
  const { data: contactCompaniesData, isLoading: isLoadingContactCompanies } = useFindAllCompaniesContactsQuery({ filters: companiesFilters, targetList: targetId });

  const { data: emailsHistoryData, isLoading: isLoadingEmailsHistory } = useFindEmailsHistoryQuery({ targetListId: targetId, limit: tab === 'people' ? peopleFilters.itemsPerPage * 2 : companiesFilters.itemsPerPage * 2 }, { skip: !targetId });
  const { data: emailTemplates } = useFindAllCustomMailsQuery({ projectId: project?.id! }, { skip: !project?.id });
  const [showDeleteBulkContactsModal, setShowDeleteBulkContactsModal] = useState(false);
  const [showSoftBulkModal, setShowSoftBulkModal] = useState(false);
  const [updateTargetList, { isLoading: isUpdatingTargetList }] = useUpdateTargetListMutation();
  const [softBulkContacts, { isLoading: isSoftBulkLoading }] = useSoftBulkContactsMutation();

  const targetListsState = useAppSelector((state) => state.targetList);
  const usersState = useAppSelector((state) => state.users);
  const [direction, setDirection] = useState<"left" | "right">("left");
  const [selectedPeopleIds, setSelectedPeopleIds] = useState<{ contactId: string, personId: string }[]>([]);
  const [showPopupPeople, setShowPopupPeople] = useState<boolean>(false);
  const [selectedCompaniesIds, setSelectedCompaniesIds] = useState<{ contactId: string, companyId: string }[]>([]);
  const [showPopupCompanies, setShowPopupCompanies] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const form = useForm<UpdateTargetListFormValues>({
    resolver: zodResolver(updateTargetListSchema),
    defaultValues: {
      name: '',
      commercialIds: [],
      revenue: "undefined:undefined:undefined",
      employeesNumber: "undefined:undefined:undefined",
      departments: [],
      roles: [],
      sectors: [],
      categories: [],
    },
  });

  useEffect(() => {
    if (targetList) {
      const formValues: Partial<UpdateTargetListFormValues> = {
        name: targetList.name,
        commercialIds: targetList.commercials.map(commercial => commercial.id) ?? [],
        revenue: targetList.revenue,
        employeesNumber: targetList.employeesNumber,
        departments: targetList.departments,
        roles: targetList.roles,
        sectors: targetList.sectors ?? [],
        categories: targetList.categories ?? [],
      };
      form.reset(formValues);
      setPeopleFilters(prev => ({ ...prev, targetList: [{ value: targetList.id, label: targetList.name }] }));
      setCompaniesFilters(prev => ({ ...prev, targetList: [{ value: targetList.id, label: targetList.name }] }));
    }
  }, [targetList, form])

  const handleSubmit = async () => {
    try {
      await updateTargetList({
        id: targetList!.id,
        data: {
          ...form.getValues(),
        },
      }).unwrap();
      dispatch(addBannerWithTimeout({
        variant: "success",
        message: "Lista target aggiornata con successo.",
      }))
    } catch (error) {
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Errore durante l'aggiornamento della lista target.",
      }));
    }
  };

  const handleSoftBulkContacts = async () => {
    try {
      await softBulkContacts({ targetListId: targetList!.id, contactType: tab === 'people' ? ContactType.PERSON : ContactType.COMPANY }).unwrap();
      setShowSoftBulkModal(false);
      dispatch(addBannerWithTimeout({
        message: 'Contatti cestinati con successo.',
        variant: 'success'
      }))
    } catch (error) {
      setShowSoftBulkModal(false);
      dispatch(addBannerWithTimeout({
        message: 'Si è verificato un errore durante il cestinamento dei contatti.',
        variant: 'error'
      }))
    }
  }

  if (isLoadingTargetList || isLoadingContactPeople || isLoadingContactCompanies) {
    return <div className="flex items-center justify-center h-screen w-full"><Spinner /></div>
  }

  return (
    <Layout
      headerLabel={targetList?.name ?
        tab === 'specifics' ? targetList?.name :
          targetList?.name.length > 17 ?
            targetList?.name.substring(0, 17) + "..." :
            targetList?.name
        : "Dettagli lista target"
      }
      headerBackPath={projectId !== 'targetlist' ? `/edit-project/${projectId}/target-lists` : "/target-list"}
      breadcrumbItems={[
        "Liste target",
        targetList?.name!,
        (tab === 'specifics' ? 'Specifiche' : tab === 'companies' ? 'Aziende' : 'Persone')
      ]}
      menuItem={MenuItems.TARGET_LIST}
      headerChildren={
        <div className="flex items-center justify-between">
          <TabMenu
            direction={direction}
            setDirection={(value) => setDirection(value)}
            setValue={(e) => {
              dispatch(setTargetListPage(TargetListPageEnum.LIST));
              if (e === 0) {
                navigate(window.location.pathname.replace(/\/people|\/companies/, "/specifics"));
              }
              if (e === 1) {
                navigate(window.location.pathname.replace(/\/specifics|\/companies/, "/people"));
              }
              if (e === 2) {
                navigate(window.location.pathname.replace(/\/specifics|\/people/, "/companies"));
              }
              // dispatch(setSelectedContact(undefined))
            }}
            value={tab === 'specifics' ? 0 : tab === 'people' ? 1 : 2}
            items={[
              {
                icon: <></>,
                label: "Specifiche lista",
              },
              {
                icon: <></>,
                label:
                  "Persone (" +
                  numericFormatter(counters?.find(count => count.contactType === ContactType.PERSON)?.dto.inList.total.toString() ?? "0", { thousandSeparator: '.' })
                  +
                  ")",
              },
              {
                icon: <></>,
                label:
                  "Aziende (" +
                  numericFormatter(counters?.find(count => count.contactType === ContactType.COMPANY)?.dto.inList.total.toString() ?? "0", { thousandSeparator: '.' })
                  +
                  ")",
              },
            ]}
          />
          <div className="flex items-center gap-2">
            {
              tab === 'people' && targetListsState.targetListPage === TargetListPageEnum.LIST &&
              <ActionsPeopleTargetList
                setSelectedIds={setSelectedPeopleIds}
                setFilters={setPeopleFilters}
                isInBin={false}
                setShowSoftBulkModal={setShowSoftBulkModal}
                setDeleteBulkContactsModal={setShowDeleteBulkContactsModal}
                targetList={targetList!}
                contacts={contactPeopleData as unknown as FindContactsTargetResponse<ContactType.PERSON>}
                filters={peopleFilters} selectedIds={selectedPeopleIds} setShowPopup={(action) => setShowPopupPeople(action)} />
            }
            {
              tab === 'companies' && targetListsState.targetListPage === TargetListPageEnum.LIST &&
              <ActionsCompaniesTargetList
                isInBin={false}
                setFilters={setCompaniesFilters}
                setSelectedIds={setSelectedCompaniesIds}
                setShowSoftBulkModal={setShowSoftBulkModal}
                setDeleteBulkContactsModal={setShowDeleteBulkContactsModal}
                targetList={targetList!}
                contacts={contactCompaniesData as unknown as {
                  total: number
                  page: number
                  perPage: number
                  prev: boolean
                  next: boolean
                  totalPage: number
                  data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
                }}
                filters={companiesFilters} selectedIds={selectedCompaniesIds} setShowPopup={(action) => setShowPopupCompanies(action)} />
            }
            {
              targetListsState.targetListPage === TargetListPageEnum.LIST && tab === 'specifics' && (usersState.findMeResponse?.isAutonomous || usersState.findMeResponse?.isAutonomous === null) && !keycloak.hasRealmRole("customer") && !keycloak.hasRealmRole("collaborator") &&
              < div className="flex items-center justify-end w-full h-full">
                <Button
                  size={"sm"}
                  iconPosition={"left"}
                  variant={"outline"}
                  color={"blue"}
                  icon={<TrashIcon color={""} size={0} />}
                  label="Visualizza Ko"
                  onClick={() => {
                    navigate('/target-list-bins/' + targetList?.id! + '/people/' + projectId);
                  }}
                />
              </div>
            }
            {
              tab === 'specifics' &&
              !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
              !(keycloak.hasRealmRole('commercial') && !usersState.findMeResponse?.isAutonomous) &&
              <div className="flex justify-end items-center gap-2">
                {
                  isUpdatingTargetList ? <Spinner /> :
                    <Button
                      size={"sm"}
                      iconPosition={"off"}
                      label="Aggiorna"
                      variant={"solid"}
                      color={"blue"}
                      onClick={() => form.handleSubmit(handleSubmit)()}
                    />
                }
              </div>
            }
          </div>
        </ div>
      }
    >
      <EditTargetList
        form={form}
        project={project!}
        targetContactIds={contactsIds ?? []}
        targetList={targetList!}

        isLoadingEmailsHistory={isLoadingEmailsHistory}
        emailsHistory={emailsHistoryData!}
        templates={emailTemplates!}

        peopleContacts={contactPeopleData as unknown as FindContactsTargetResponse<ContactType.PERSON>}
        companiesContacts={contactCompaniesData as unknown as {
          total: number
          page: number
          perPage: number
          prev: boolean
          next: boolean
          totalPage: number
          data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
        }}

        peopleCounters={counters?.find(count => count.contactType === ContactType.PERSON)!}
        companiesCounters={counters?.find(count => count.contactType === ContactType.COMPANY)!}

        companiesFilters={companiesFilters}
        setCompaniesFilters={act => setCompaniesFilters({ ...companiesFilters, ...act })}
        peopleFilters={peopleFilters}
        setPeopleFilters={act => setPeopleFilters((prev) => ({ ...prev, ...act }))}

        selectedIdsPeople={selectedPeopleIds}
        setSelectedIdsPeople={arg => setSelectedPeopleIds(arg)}
        setShowPopupPeople={arg => setShowPopupPeople(arg)}
        showPopupPeople={showPopupPeople}
        selectedIdsCompanies={selectedCompaniesIds}
        setSelectedIdsCompanies={arg => setSelectedCompaniesIds(arg)}
        setShowPopupCompanies={arg => setShowPopupCompanies(arg)}
        showPopupCompanies={showPopupCompanies}
        //TODO LOAD TO ACTIVATE
        toActivatePeople={0}
        toActivateCompanies={0}
      />
      <DeleteBulkContacts
        filters={tab === 'people' ? peopleFilters : companiesFilters}
        setFilters={tab === 'people' ? setPeopleFilters : setCompaniesFilters}
        showModal={showDeleteBulkContactsModal}
        setShowModal={setShowDeleteBulkContactsModal}
        contactType={tab === 'people' ? ContactType.PERSON : ContactType.COMPANY}
        section={"target"}
        targetId={targetId}
      />
      {/* <ConfirmModal
        loading={contactsState.deleteBulkStatus === "loading"}
        title={'Elimina contatti'}
        label={
          "Sei sicuro di voler eliminare tutti i contatti trovati?"
        }
        confirmAction={function (): void {
          const filters = tab === 'companies' ? companiesFilters : peopleFilters
          const contactType = tab === 'companies' ? ContactType.COMPANY : ContactType.PERSON
          dispatch(deleteBulk({ targetListId: targetList?.id!, filters, contactType }))
        }}
        onClose={function (): void {
          dispatch(setBulkDeleteModal(false))
        }}
        visible={databaseState.bulkDeleteModal}
      /> */}
      <ConfirmModal
        loading={isSoftBulkLoading}
        title={'Cestina contatti'}
        label={
          "Sei sicuro di voler cestinare tutti i contatti trovati?"
        }
        confirmAction={function (): void {
          handleSoftBulkContacts()
        }}
        onClose={function (): void {
          setShowSoftBulkModal(false)
        }}
        visible={showSoftBulkModal}
      />
    </Layout>
  );
}
